// InfoMulchen.js – Leistungsseite /mulchen (Landschaftspflege, Mulchen, Rückearbeiten): Hero, Fachtexte, Karussell.
import "./Home.css";
import "./custom.css";
import usePageMeta from "../../usePageMeta";
import { JsonLd, leistungSchema } from "../Strukturdaten";
import betrieb from "../../data/betrieb";
import { Link } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Carousel from "react-bootstrap/Carousel";
import { karussellIntervall } from "../../reducedMotion";

import Gewaesser from "./images/Raupe/gewaesserpflege.webp";
import Forstmulcher from "./images/Raupe/Forstmulcher.webp";
import MulcherVorher from "./images/VorherNacher/MulcherVorher.webp";
import MulcherNachher from "./images/VorherNacher/MulcherNachher.webp";
import BeforeAfter from "./BeforeAfter";

const facts = [
  {
    title: "Magerrasen: Hotspot der Artenvielfalt",
    text: "Ein Quadratmeter artenreicher Magerrasen kann bis zu 40 verschiedene Pflanzenarten beherbergen. Diese Vielfalt entsteht nur auf nährstoffarmen, offenen Böden und ist auf regelmäßige Pflege angewiesen, da ohne Eingriff die Sukzession einsetzt.",
  },
  {
    title: "Sukzession: Die Natur strebt zum Wald",
    text: "Offenlandflächen in Mitteleuropa sind keine stabilen Endzustände. Ohne Pflege wandeln sich Magerrasen und Heiden innerhalb von 10–20 Jahren über Gebüschstadien in Vorwald um, und die daran gebundenen Arten verschwinden.",
  },
  {
    title: "Wildbienen brauchen Offenland",
    text: "Viele heimische Wildbienenarten sind auf offene, sonnenexponierte Flächen mit lückiger Vegetation angewiesen. Abschnittsweises Mulchen erhält genau diese Strukturen dauerhaft.",
  },
  {
    title: "Mulch schützt den Boden",
    text: "Das zurückbleibende Häckselmaterial verringert die Verdunstung, fördert das Bodenleben und verhindert Oberflächenerosion an Hanglagen und Böschungen.",
  },
  {
    title: "Knicks: geschützte Wallhecken",
    text: "Die Knicks Schleswig-Holsteins sind gesetzlich geschützt. Damit sie dicht und artenreich bleiben, werden sie etwa alle 10 bis 15 Jahre abschnittsweise auf den Stock gesetzt, außerhalb der Brutzeit und so, dass Überhälter stehen bleiben.",
  },
  {
    title: "Abschnittsweises Mulchen als Artenschutzmaßnahme",
    text: "Reptilien, Feldgrillen und bodenbrütende Insekten benötigen Rückzugsflächen beim Pflegeeingriff. Durch gezieltes Aussparen von Teilbereichen und jährlichen Wechsel entstehen dauerhaft strukturreiche Mosaike.",
  },
];

// Strukturdaten dieser Leistung; nur Inhalte, die unten auf der Seite stehen. Bild: public/og-image.jpg zeigt die Raupe mit Forstmulcher.
const schema = leistungSchema({
  pfad: "/mulchen",
  name: "Landschaftspflege", // wie H1, Menü und Leistungskatalog (LEISTUNGEN in Strukturdaten.js)
  alternateName: "Flächenpflege",
  serviceType: "Landschaftspflege durch Mulchen",
  beschreibung:
    "Bodenschonende Landschaftspflege durch Mulchen mit dem funkgesteuerten Geräteträger Moritz FR 75: Magerrasen, " +
    "Böschungen, Gewässerrandstreifen, Deiche und Naturschutzflächen, auch auf steilen, feuchten oder sonst unzugänglichen " +
    "Flächen mit minimalem Bodendruck. Abschnittsweise Pflege außerhalb der Brut- und Aufzuchtzeiten (§ 44 BNatSchG), " +
    "bevorzugt im Herbst und Winter; das zerkleinerte Material bleibt als Mulchschicht auf der Fläche. " +
    "Individuelles Pflegekonzept für Böschung, Naturschutz- oder Grünfläche.",
  leistungen: [
    "Mulchen von Böschungen und Gewässerrandstreifen",
    "Pflege von Magerrasen und Naturschutzflächen",
    "Uferpflege mit der funkgesteuerten Raupe",
    "Zurückdrängen von Gehölzaufwuchs mit dem Forstmulcher",
    "Pflegekonzept für Böschung, Naturschutz- oder Grünfläche",
  ],
  bild: {
    "@type": "ImageObject",
    url: `${betrieb.website}/og-image.jpg`,
    width: 1200,
    height: 630,
    caption: "Funkgesteuerte Raupe Moritz FR 75 mit Forstmulcher in dichtem Gehölzaufwuchs",
  },
});

function InfoMulchen() {
  usePageMeta({
    path: "/mulchen",
    title: "Landschaftspflege & Mulchen für Böschungen | Forst & Flur",
    description:
      "Bodenschonendes Mulchen von Böschungen, Magerrasen, Gewässerrändern und Naturschutzflächen mit funkgesteuerter Raupe: abschnittsweise Pflege für Artenvielfalt.",
  });

  return (
    <div>
      <JsonLd daten={schema} />
      {/* Hero */}
      <div className="ff-page-hero">
        <div className="ff-page-hero__content">
          <span className="ff-eyebrow">Forst &amp; Flur</span>
          <h1 className="ff-page-hero__title">Landschafts&shy;pflege</h1>
          <p className="ff-page-hero__sub">
            Natürliche Kreisläufe nutzen: Flächen pflegen, ohne zu belasten.
          </p>
        </div>
      </div>

      {/* Artikel-Body */}
      <div className="custom-dark-green text-white">
        <Container className="py-5">
          <Row className="justify-content-center">
            <Col xs={12} lg={10}>

              <h2 className="ff-article-h2">
                Offenland erhalten:<br />mit Methode und Rücksicht
              </h2>

              <p className="fs-5 text-shadow">
                Die meisten Offenlandflächen Mitteleuropas, also Magerrasen, Heiden, Wiesenbrachen,
                Böschungen und Gewässerrandstreifen, sind keine natürlichen Gleichgewichtszustände.
                Sie entstanden durch Jahrhunderte extensiver Bewirtschaftung und brauchen aktive
                Pflege, um ihre außergewöhnliche Artenvielfalt zu erhalten.
              </p>

              <Row className="align-items-start my-4 gy-3">
                <Col xs={12} md={7}>
                  <p className="fs-5 text-shadow">
                    Mulchen ist ein wirkungsvolles Instrument der Landschaftspflege: Im Gegensatz
                    zum klassischen Mähen mit Abtransport verbleibt das zerkleinerte Pflanzenmaterial
                    auf der Fläche. Es deckt den Boden, schützt vor Austrocknung und Erosion und
                    gibt Nährstoffe langsam zurück.
                  </p>
                  <p className="fs-5 text-shadow mt-3">
                    Auf nährstoffarmen Standorten ist die Standortanalyse entscheidend, denn nicht
                    überall ist Mulchen die richtige Methode. Mit dem Rückgang traditioneller
                    Nutzungsformen beginnt die Verbuschung, und mit ihr der Verlust spezialisierter
                    Lebensgemeinschaften.
                  </p>
                </Col>
                <Col xs={12} md={5}>
                  <img
                    src={Gewaesser}
                    width={1200}
                    height={675}
                    alt="Funkgesteuerte Raupe Moritz FR 75 mit Mulchkopf am Ufer eines Teichs mit Röhricht"
                    className="img-fluid rounded w-100"
                    style={{ objectFit: "cover", maxHeight: "360px" }}
                    loading="lazy"
                    decoding="async"
                  />
                  <p className="text-white mt-2 fs-6 fst-italic text-center">
                    Uferpflege am Teich mit der funkgesteuerten Raupe: bodenschonend bis an die Wasserkante
                  </p>
                </Col>
              </Row>

              <h3 className="ff-article-h3">
                Vorteile gegenüber schwerer Technik
              </h3>
              <p className="fs-5 text-shadow">
                Auf Naturschutzflächen, Deichen und Gewässerböschungen gelten oft strenge Auflagen:
                wenig Bodendruck, keine Spurrillen, Rücksicht auf Deckschicht und Uferkante. Unser
                Maschinenkonzept ist genau darauf ausgelegt und kommt dorthin, wo ein Traktor mit
                Mulcher nicht mehr sicher fahren kann.
              </p>

              <Row className="align-items-start my-4 gy-3">
                <Col xs={12} md={5}>
                  <img
                    src={Forstmulcher}
                    width={1200}
                    height={675}
                    alt="Raupe Moritz FR 75 mit Forstmulcher in dichtem Gehölzaufwuchs"
                    className="img-fluid rounded w-100"
                    style={{ objectFit: "cover", maxHeight: "360px" }}
                    loading="lazy"
                    decoding="async"
                  />
                  <p className="text-white mt-2 fs-6 fst-italic text-center">
                    Forstmulcher bei der strukturierten Zurückdrängung von Gehölzaufwuchs
                  </p>
                </Col>
                <Col xs={12} md={7}>
                  <p className="fs-5 text-shadow">
                    Gegenüber schwerem Mulchgerät auf Traktorbasis bietet unser Geräteträger
                    Moritz FR 75 deutliche Vorteile: minimaler Bodendruck, kaum Spurschäden,
                    Einsatz auch auf steilen, feuchten oder sonst unzugänglichen Flächen.
                  </p>
                  <p className="fs-5 text-shadow mt-3">
                    Mulcharbeiten in sensiblen Bereichen finden bei uns außerhalb der Hauptbrut-
                    und Aufzuchtzeiten statt, bevorzugt im Herbst und Winter (§ 44 BNatSchG).
                    Ergänzend wenden wir das Prinzip der abschnittsweisen Pflege an, um strukturreiche
                    Mosaike und Rückzugsräume für Reptilien, Insekten und Wiesenvögel zu erhalten.
                  </p>
                </Col>
              </Row>

              <h3 className="ff-article-h3">Vorher und nachher: das Ergebnis</h3>

              <div className="ff-ba-panel">
                <div className="ff-ba-panel__media">
                  <BeforeAfter
                    before={MulcherVorher}
                    width={675}
                    height={1197}
                    after={MulcherNachher}
                    beforeAlt="Vorher: dichter Aufwuchs aus Gehölzen, Brombeeren und hohem Gras am Waldrand"
                    afterAlt="Nachher: gemulchte, freigestellte Fläche vor den erhaltenen Altbäumen, der Geräteträger steht links im Hintergrund"
                    caption="Regler ziehen oder auf „Vorher“ und „Nachher“ tippen"
                  />
                </div>

                <div className="ff-ba-panel__body">
                  <span className="ff-eyebrow">Dieselbe Stelle, zwei Zustände</span>
                  <p className="ff-ba-panel__lead">
                    Links der zugewachsene Gehölzrand, rechts nach dem Mulchen: Der Aufwuchs ist
                    zurückgenommen, die Altbäume bleiben stehen, und das zerkleinerte Material
                    verbleibt als Mulchschicht auf der Fläche.
                  </p>

                  <ul className="ff-checklist">
                    <li>
                      <strong>Bodenschonend</strong>
                      <span>Minimaler Bodendruck, kaum Spurschäden, auch auf steilen oder feuchten Flächen.</span>
                    </li>
                    <li>
                      <strong>Bäume bleiben</strong>
                      <span>Gepflegt wird der Aufwuchs, nicht der Bestand: Altbäume bleiben erhalten.</span>
                    </li>
                    <li>
                      <strong>Naturverträglich</strong>
                      <span>Abschnittsweise Pflege lässt Rückzugsräume für Reptilien, Insekten und Wiesenvögel.</span>
                    </li>
                    <li>
                      <strong>Zur richtigen Zeit</strong>
                      <span>Außerhalb der Brut- und Aufzuchtzeiten, bevorzugt im Herbst und Winter (§ 44 BNatSchG).</span>
                    </li>
                  </ul>
                </div>
              </div>

              <p className="fs-5 text-shadow mt-4">
                Sie benötigen Flächenpflege, die naturverträglich, termingerecht und rechtssicher
                ist? Wir erstellen ein individuelles Pflegekonzept für Ihre Böschung, Naturschutz-
                oder Grünfläche: bodenschonend, ohne unnötige Bodenverdichtung und gemäß BNatSchG.
              </p>

              <div className="text-center mt-4">
                <Link to="/kontakt" className="btn-ff-primary" onClick={() => window.scrollTo(0, 0)}>
                  Flächenpflege anfragen
                </Link>
              </div>

              <div className="ff-cross-links">
                <p>Weitere Leistungen von Forst &amp; Flur</p>
                <div className="d-flex flex-wrap justify-content-center gap-2">
                  <Link to="/baumpflege" className="btn-ff-outline" onClick={() => window.scrollTo(0, 0)}>
                    Baumpflege
                  </Link>
                  <Link to="/baumfaellung" className="btn-ff-outline" onClick={() => window.scrollTo(0, 0)}>
                    Baumfällung
                  </Link>
                </div>
              </div>

            </Col>
          </Row>
        </Container>
      </div>

      {/* Fakten-Karussell */}
      <div className="hero-container text-white">
        <Container className="py-5">
          <Row className="justify-content-center text-center mb-4">
            <Col xs={12} lg={9}>
              <span className="ff-eyebrow">Wissenswertes</span>
              <h2 className="ff-section-title" style={{ marginBottom: ".5rem" }}>Gut zu wissen</h2>
              <p style={{ color: "var(--ff-muted)" }}>
                Fakten rund um Mulchen, Offenland und Naturschutz
              </p>
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col xs={12} lg={9}>
              <Carousel
                interval={karussellIntervall(6000)}
                indicators={true}
                prevLabel="Vorherige Folie"
                nextLabel="Nächste Folie"
                indicatorLabels={facts.map((_, i) => `Folie ${i + 1}`)}
              >
                {facts.map((fact, index) => (
                  <Carousel.Item key={index}>
                    <div
                      className="text-center px-3 px-md-5 pb-5"
                      style={{ minHeight: "240px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}
                    >
                      <div className="p-4 p-md-5 w-100" style={{ background: "rgba(7,26,16,.65)", borderRadius: "3px", border: "1px solid rgba(159,211,86,.15)" }}>
                        <h3 style={{ fontFamily: "var(--fh)", fontWeight: 800, fontSize: "1.5rem", textTransform: "uppercase", color: "var(--ff-grass)", marginBottom: "1rem" }}>
                          {fact.title}
                        </h3>
                        <p className="fs-5 text-white text-shadow mb-0">{fact.text}</p>
                      </div>
                    </div>
                  </Carousel.Item>
                ))}
              </Carousel>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
}

export default InfoMulchen;
